import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "w-full rounded-md bg-[#141417] border border-[#27272A] px-3 py-2.5 text-white outline-none focus:border-[#D4AF37] text-sm";
const ROLES = ["OWNER", "EVENT_MANAGER", "DOOR_STAFF"];

export default function Settings() {
  const [s, setS] = useState(null);
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "", role: "DOOR_STAFF" });

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/auth/staff").then((r) => setStaff(r.data)).catch(() => {});
  }, []);

  if (!s) return <div className="text-zinc-500">Loading…</div>;

  const setField = (path, val) => setS((prev) => {
    const copy = JSON.parse(JSON.stringify(prev));
    let o = copy; const parts = path.split(".");
    for (let i = 0; i < parts.length - 1; i++) { o[parts[i]] = o[parts[i]] || {}; o = o[parts[i]]; }
    o[parts[parts.length - 1]] = val; return copy;
  });

  const save = async () => {
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addStaff = async () => {
    try { await api.post("/auth/staff", newStaff); toast.success("Staff added");
      setNewStaff({ name: "", email: "", password: "", role: "DOOR_STAFF" });
      const r = await api.get("/auth/staff"); setStaff(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delStaff = async (id) => { await api.delete(`/auth/staff/${id}`); const r = await api.get("/auth/staff"); setStaff(r.data); };

  const reseedDemo = async () => { try { await api.post("/admin/demo/seed"); toast.success("Demo data seeded"); } catch (e) { toast.error("Failed"); } };
  const clearDemo = async () => { if (!window.confirm("Remove all DEMO events & venues?")) return; try { const { data } = await api.delete("/admin/demo/clear"); toast.success(`Removed ${data.removed_events} demo events`); } catch (e) { toast.error("Failed"); } };

  const Card = ({ title, children }) => (
    <div className="rounded-xl border border-[#27272A] bg-[#18181B] p-4 mb-4"><h2 className="font-oswald text-sm tracking-widest text-zinc-300 uppercase mb-3">{title}</h2>{children}</div>
  );

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl uppercase text-white mb-6">Settings</h1>

      <Card title="Fees & Tax">
        <div className="grid grid-cols-3 gap-3">
          <div><label className="text-[10px] text-zinc-500">Service Fee %</label><input type="number" step="0.1" className={field} value={s.service_fee_percent} onChange={(e) => setField("service_fee_percent", parseFloat(e.target.value) || 0)} /></div>
          <div><label className="text-[10px] text-zinc-500">Flat Fee $</label><input type="number" step="0.01" className={field} value={s.service_fee_flat} onChange={(e) => setField("service_fee_flat", parseFloat(e.target.value) || 0)} /></div>
          <div><label className="text-[10px] text-zinc-500">Tax %</label><input type="number" step="0.1" className={field} value={s.tax_percent} onChange={(e) => setField("tax_percent", parseFloat(e.target.value) || 0)} /></div>
        </div>
      </Card>

      <Card title="About Page">
        <input className={field} placeholder="Headline" value={s.about?.headline || ""} onChange={(e) => setField("about.headline", e.target.value)} />
        <textarea rows={5} className={`${field} mt-2`} placeholder="About body" value={s.about?.body || ""} onChange={(e) => setField("about.body", e.target.value)} />
      </Card>

      <Card title="Contact & Social">
        <div className="grid grid-cols-2 gap-2">
          <input className={field} placeholder="Email" value={s.contact?.email || ""} onChange={(e) => setField("contact.email", e.target.value)} />
          <input className={field} placeholder="Phone" value={s.contact?.phone || ""} onChange={(e) => setField("contact.phone", e.target.value)} />
          <input className={field} placeholder="Address" value={s.contact?.address || ""} onChange={(e) => setField("contact.address", e.target.value)} />
          <input className={field} placeholder="Instagram URL" value={s.contact?.instagram || ""} onChange={(e) => setField("contact.instagram", e.target.value)} />
          <input className={field} placeholder="Facebook URL" value={s.contact?.facebook || ""} onChange={(e) => setField("contact.facebook", e.target.value)} />
          <input className={field} placeholder="YouTube URL" value={s.contact?.youtube || ""} onChange={(e) => setField("contact.youtube", e.target.value)} />
        </div>
      </Card>

      <Card title="Legal Pages (editable)">
        <label className="text-[10px] text-zinc-500">Privacy Policy</label>
        <textarea rows={3} className={field} value={s.legal?.privacy || ""} onChange={(e) => setField("legal.privacy", e.target.value)} />
        <label className="text-[10px] text-zinc-500 mt-2 block">Terms & Conditions</label>
        <textarea rows={3} className={field} value={s.legal?.terms || ""} onChange={(e) => setField("legal.terms", e.target.value)} />
        <label className="text-[10px] text-zinc-500 mt-2 block">Refund Policy</label>
        <textarea rows={3} className={field} value={s.legal?.refund || ""} onChange={(e) => setField("legal.refund", e.target.value)} />
      </Card>

      <Card title="Analytics (tracking IDs)">
        <div className="grid grid-cols-2 gap-2">
          <input className={field} placeholder="Google Analytics ID" value={s.analytics?.ga_id || ""} onChange={(e) => setField("analytics.ga_id", e.target.value)} />
          <input className={field} placeholder="Meta Pixel ID" value={s.analytics?.meta_pixel_id || ""} onChange={(e) => setField("analytics.meta_pixel_id", e.target.value)} />
        </div>
      </Card>

      <button data-testid="save-settings-btn" onClick={save} className="btn-gold w-full rounded-md py-3 font-oswald tracking-widest mb-6">SAVE SETTINGS</button>

      <Card title="Staff & Roles">
        <div className="space-y-2 mb-4">
          {staff.map((u) => (
            <div key={u.id} className="flex items-center justify-between rounded-md border border-[#27272A] px-3 py-2">
              <div><p className="text-sm text-white">{u.name} <span className="text-zinc-500 text-xs">· {u.email}</span></p><span className="text-[10px] font-oswald tracking-widest text-[#D4AF37]">{u.role}</span></div>
              <button onClick={() => delStaff(u.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="staff-name" className={field} placeholder="Name" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} />
          <input data-testid="staff-email" className={field} placeholder="Email" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} />
          <input data-testid="staff-password" className={field} type="text" placeholder="Password" value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} />
          <Select value={newStaff.role} onValueChange={(v) => setNewStaff({ ...newStaff, role: v })}>
            <SelectTrigger className={field}><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <button data-testid="add-staff-btn" onClick={addStaff} className="mt-3 flex items-center gap-1.5 text-sm text-[#D4AF37] hover:underline"><Plus size={15} /> Add staff member</button>
      </Card>

      <Card title="Demo Content">
        <p className="text-xs text-zinc-500 mb-3">Demo events & venues are clearly marked. Remove them when you're ready to go live.</p>
        <div className="flex gap-2">
          <button data-testid="reseed-demo-btn" onClick={reseedDemo} className="flex items-center gap-1.5 rounded-md border border-[#27272A] px-3 py-2 text-xs text-white hover:border-[#D4AF37]"><RefreshCw size={14} /> Seed Demo</button>
          <button data-testid="clear-demo-btn" onClick={clearDemo} className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-xs text-white"><Trash2 size={14} /> Remove All Demo</button>
        </div>
      </Card>
    </div>
  );
}
